import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Col, Input, Label, Row } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import {
  fetchOptionsByTravelType,
  fetchTravelType,
} from '../../../store/apis/travelService';
import { AsyncAutocompleteSelect } from '../../Common';
import { fetchDynamicData } from '../../../store/apis/autocompleteService';

function GeneralInformation({
  detailsData,
  isEdit,
  isView = false,
  control,
  errors,
  setValue,
  watch,
  travelType,
  toggleFamilyDetailsModal,
  isCommonOptionsLoading,
  commonOptions,
  defaultData,
}) {
  const costAssignment = watch('costAssignment');

  // Autopopulating the fields when editing a travel request
  useEffect(() => {
    if (isEdit && detailsData?.headerDetails) {
      const headerDetails = detailsData?.headerDetails;
      const travelStatus =
        headerDetails?.TravelStatus?.toString()?.trim() ?? null;
      setValue('travelType', headerDetails?.TravelType?.trim() ?? '');
      setValue('travellerType', headerDetails?.TravelerType?.trim() ?? '');
      setValue('budgeted', headerDetails?.Budgeted?.trim() ?? '');
      setValue('costAssignment', headerDetails?.CostAssignment?.trim() ?? '');
      setValue('bookingAgent', headerDetails?.BookingResponsible?.trim() ?? '');
      setValue('purpose', headerDetails?.TravelPurpouse?.trim() ?? '');
      setValue(
        'comments',
        travelStatus === '15' || travelStatus === '10'
          ? (headerDetails?.QuoteCommets?.trim() ?? '')
          : (headerDetails?.TravelComments?.trim() ?? ''),
      );
      setValue('department', headerDetails?.CC?.trim() ?? '');

      const familyInformation = detailsData?.travelerDetails;
      const mapped = familyInformation.map(item => ({
        relationship: item?.Relationship?.trim(),
        dob: item?.DOB?.trim()?.split('T')?.[0],
        passport: item?.PassportNo?.trim(),
        lastName: item?.Traveler_LName?.trim(),
        middleName: item?.Traveler_MName?.trim(),
        firstName: item?.Traveler_FName?.trim(),
      }));
      setValue('familyInformation', mapped);
    } else if (defaultData?.[0]?.['Booking Responsible']) {
      setValue('bookingAgent', defaultData?.[0]?.['Booking Responsible']);
    }
  }, [defaultData, detailsData, isEdit, setValue, travelType]);

  useEffect(() => {
    if (!isEdit) return;

    const headerDetails = detailsData?.headerDetails;
    if (!headerDetails) return;

    if (costAssignment === 'B1C03') {
      const sor = headerDetails?.SORNo?.trim();
      setValue('sorNo', sor ? { label: sor, value: sor } : null);
    } else if (costAssignment === 'B1C04') {
      const order = headerDetails?.OrderNo?.trim();
      setValue('serviceOrder', order ? { label: order, value: order } : null);
    } else if (['B1C02', 'T1C04'].includes(costAssignment)) {
      setValue('department', headerDetails?.CC?.trim() || '');
    } else {
      const sor = headerDetails?.SORNo?.trim();
      setValue('sorNo', sor || '');
    }
  }, [costAssignment, isEdit, setValue, detailsData]);

  const { data: travelTypes = [], isLoading: isTravelTypeLoading } = useQuery({
    queryKey: ['travelTypes'],
    queryFn: fetchTravelType,
    staleTime: 5 * 60 * 1000, // optional: cache for 5 minutes
  });

  const {
    data: optionsByTravelType = [],
    isLoading: isOptionsByTravelTypeLoading,
  } = useQuery({
    queryKey: ['populateCommonOptions', travelType],
    queryFn: () => fetchOptionsByTravelType(travelType),
    enabled: !!travelType, // Only run when travelType is selected
  });

  return (
    <div className="new-travel-request-card w-100 p-3 bg-white">
      <div className="fw-semibold mb-2 fs-5">General Details</div>
      <Row>
        <Col xs={12} md={4} className="mb-3">
          <Label>
            Travel Type<span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="travelType"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  className={`form-select external-filter-input ${errors.travelType && 'is-invalid'}`}
                  type="select"
                  disabled={isTravelTypeLoading || isView}
                  title="Select the type of travel from the available options in the dropdown list."
                >
                  <option value="">Select Travel Type</option>
                  {travelTypes.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
                {errors.travelType && (
                  <div className="text-danger-tr">
                    {errors.travelType.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={4} className="mb-3">
          <Label>
            Type of Traveller
            <span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="travellerType"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  className={`form-select external-filter-input ${errors.travellerType && 'is-invalid'}`}
                  type="select"
                  disabled={isOptionsByTravelTypeLoading || isView}
                  title="Choose the type of traveller. The dropdown options are changed based on the 'Travel Type' selection."
                >
                  <option value="">Select Type of Traveller</option>
                  {optionsByTravelType?.travelerType?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
                {errors.travellerType && (
                  <div className="text-danger-tr">
                    {errors.travellerType.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={4} className="mb-3">
          <Label>
            Budgeted<span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="budgeted"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  className={`form-select external-filter-input ${errors.budgeted && 'is-invalid'}`}
                  disabled={isView}
                  type="select"
                  title="Select whether the travel is budgeted or not."
                >
                  <option value="">Select Budgeted</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Input>
                {errors.budgeted && (
                  <div className="text-danger-tr">
                    {errors.budgeted.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={4} className="mb-3">
          <Label>
            Cost Assignment<span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="costAssignment"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  className={`form-select external-filter-input ${errors.costAssignment && 'is-invalid'}`}
                  type="select"
                  disabled={isOptionsByTravelTypeLoading || isView}
                  title="Select the type of cost assignment from the available dropdown options."
                >
                  <option value="">Select Cost Assignment</option>
                  {optionsByTravelType?.costAssignment?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
                {errors.costAssignment && (
                  <div className="text-danger-tr">
                    {errors.costAssignment.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        {costAssignment === 'B1C03' && (
          <Col xs={12} md={4} className="mb-3">
            <Label>
              SOR No.<span className="text-danger-tr">*</span>
            </Label>
            <Controller
              control={control}
              name="sorNo"
              render={({ field }) => (
                <AsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchDynamicData({
                      prefixText: input,
                      paramText: 'SOR',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Code,
                      label: item.Name,
                    }))
                  }
                  value={field.value}
                  onChange={option =>
                    setValue('sorNo', option, {
                      shouldValidate: true,
                    })
                  }
                  placeholder="SOR No."
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                    control: base => ({ ...base, borderRadius: '8px' }),
                  }}
                  title="Enter SOR No. for your travel"
                  disabled={isView}
                />
              )}
            />
            {errors.sorNo && (
              <div className="text-danger-tr">{errors.sorNo.message}</div>
            )}
          </Col>
        )}
        {costAssignment === 'B1C04' && (
          <Col xs={12} md={4} className="mb-3">
            <Label>
              Service Order<span className="text-danger-tr">*</span>
            </Label>
            <Controller
              control={control}
              name="serviceOrder"
              render={({ field }) => (
                <AsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchDynamicData({
                      prefixText: input,
                      paramText: 'SERVICE_ORDERNO_ALTER',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Code,
                      label: item.Name,
                    }))
                  }
                  value={field.value}
                  onChange={option =>
                    setValue('serviceOrder', option, {
                      shouldValidate: true,
                    })
                  }
                  placeholder="Service Order"
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                    control: base => ({ ...base, borderRadius: '8px' }),
                    placeholder: base => ({
                      ...base,
                      fontSize: '30px', // Set placeholder font size
                      color: '#ff0000', // Optional: lighter color for placeholder
                    }),
                  }}
                  title="Enter Service Order"
                  disabled={isView}
                />
              )}
            />
            {errors.serviceOrder && (
              <div className="text-danger-tr">
                {errors.serviceOrder.message}
              </div>
            )}
          </Col>
        )}
        {(costAssignment === 'B1C02' || costAssignment === 'T1C04') && (
          <Col xs={12} md={4} className="mb-3">
            <Label>
              Department<span className="text-danger-tr">*</span>
            </Label>
            <Controller
              control={control}
              name="department"
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    className={`form-select external-filter-input ${errors.department && 'is-invalid'}`}
                    type="select"
                    disabled={isCommonOptionsLoading || isView}
                    title="Enter Department"
                  >
                    <option value="">Select Department</option>
                    {commonOptions?.departments?.map(type => (
                      <option key={type.Code} value={type.Code}>
                        {type.Description}
                      </option>
                    ))}
                  </Input>
                  {errors.department && (
                    <div className="text-danger-tr">
                      {errors.department.message}
                    </div>
                  )}
                </>
              )}
            />
          </Col>
        )}
        {!['B1C03', 'B1C04', 'B1C02', 'T1C04'].includes(costAssignment) && (
          <Col xs={12} md={4} className="mb-3">
            <Label>SOR No.</Label>
            <Controller
              control={control}
              name="sorNo"
              render={({ field }) => (
                <Input
                  {...field}
                  className="form-control external-filter-input"
                  placeholder="SOR No."
                  readOnly
                  title="Enter SOR No. for your travel"
                  disabled={isView}
                />
              )}
            />
          </Col>
        )}
        <Col xs={12} md={4} className="mb-3">
          <Label>
            Booking Agent<span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="bookingAgent"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  className={`form-select external-filter-input ${errors.bookingAgent && 'is-invalid'}`}
                  type="select"
                  disabled={isCommonOptionsLoading || isView}
                  title="Select the booking agent who will handle the travel bookings."
                >
                  <option value="">Select Booking Agent</option>
                  {commonOptions?.bookingAgent?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
                {errors.bookingAgent && (
                  <div className="text-danger-tr">
                    {errors.bookingAgent.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={4} className="mb-3">
          <Label className="d-flex flex-row align-items-center gap-1">
            <span>Traveler First Name (As Per Passport)</span>
            <span className="text-danger-tr">*</span>
            <button
              type="button"
              onClick={() => toggleFamilyDetailsModal()}
              className="new-family-btn"
              disabled={isView}
            >
              +
            </button>
          </Label>
          <Controller
            control={control}
            name="travellerFirstName"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  placeholder="First Name"
                  className="external-filter-input"
                  title="Enter the traveler's first name exactly as shown on the passport."
                  disabled={isView}
                />
                {errors.travellerFirstName && (
                  <div className="text-danger-tr">
                    {errors.travellerFirstName.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={4} className="mb-3">
          <Label>Traveler Middle Name (As Per Passport)</Label>
          <Controller
            control={control}
            name="travellerMiddleName"
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Middle Name"
                className="external-filter-input"
                title="Enter the traveler's middle name exactly as on the passport (if any)."
                disabled={isView}
              />
            )}
          />
        </Col>
        <Col xs={12} md={4} className="mb-3">
          <Label>
            Traveler Last Name (As Per Passport)
            <span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="travellerLastName"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  placeholder="Last Name"
                  className="external-filter-input"
                  title="Enter the traveler's last name exactly as on the passport."
                  disabled={isView}
                />
                {errors.travellerLastName && (
                  <div className="text-danger-tr">
                    {errors.travellerLastName.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={6} className="mb-3">
          <Label>
            Purpose of Travel<span className="text-danger-tr">*</span>
          </Label>
          <Controller
            control={control}
            name="purpose"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  placeholder="Purpose"
                  className={`external-filter-input ${errors.purpose && 'is-invalid'}`}
                  rows="2"
                  type="textarea"
                  title="Briefly describe the purpose or reason for the travel."
                  disabled={isView}
                />
                {errors.purpose && (
                  <div className="text-danger-tr">{errors.purpose.message}</div>
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} md={6} className="mb-3">
          <Label>Comments</Label>
          <Controller
            control={control}
            name="comments"
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  placeholder="Comments"
                  rows="2"
                  type="textarea"
                  className={`external-filter-input ${errors.comments && 'is-invalid'}`}
                  title="Add any additional notes or special instructions for this travel request."
                  disabled={isView}
                />
                {errors.comments && (
                  <div className="text-danger-tr">
                    {errors.comments.message}
                  </div>
                )}
              </>
            )}
          />
        </Col>
      </Row>
    </div>
  );
}

export default GeneralInformation;
